import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../../Styles/NotesFollowUpForm.css";
import { notesFollowupsAPI } from "../../api/index"; // adjust path to match your project

// Shared mandal id -> name lookup. CONFIRM these ids match your actual
// `mandals` table rows — these are placeholders in dropdown order.
export const MANDALS = [
  { id: 1, name: "Kandukur" },
  { id: 2, name: "Lingasamudram" },
  { id: 3, name: "Gudluru" },
  { id: 4, name: "Ulavapadu" },
  { id: 5, name: "Voletivaripalem" },
];

// UI label <-> backend value maps
const STATUS_TO_BACKEND = { Open: "open", "In Progress": "in_progress", Completed: "closed" };
const PRIORITY_TO_BACKEND = { High: "high", Medium: "medium", Low: "low" };

const CATEGORIES = [
  "Grievance Follow-up",
  "Scheme Follow-up",
  "Development Work",
  "General",
];
const PRIORITIES = ["High", "Medium", "Low"];
const STATUSES = ["Open", "In Progress", "Completed"];

const INITIAL_FORM = {
  subject: "",
  person: "",
  voterId: "",
  mobile: "",
  category: "",
  priority: "",
  dueDate: "",
  status: "",
  village: "",
  mandalId: "",
  description: "",
};

// Order used to focus the first invalid field on submit (same as the form layout)
const FIELD_ORDER = [
  "subject",
  "person",
  "voterId",
  "mobile",
  "category",
  "priority",
  "dueDate",
  "status",
  "village",
  "mandalId",
  "description",
];

// These fields show a format error immediately while typing
const LIVE_VALIDATE_FIELDS = ["voterId", "village"];

const SUBJECT_REGEX = /^[A-Za-z0-9\s.,\-&'()/:;?!]+$/;
const NAME_REGEX = /^[A-Za-z\s.']+$/;
const VILLAGE_REGEX = /^[A-Za-z\s]+$/;
const VOTER_ID_REGEX = /^[A-Z]{3}[0-9]{7}$/;
const MOBILE_REGEX = /^[6-9][0-9]{9}$/;

// ---------- Field validation rules ----------
const validateField = (name, value) => {
  const v = typeof value === "string" ? value.trim() : value;

  switch (name) {
    case "subject":
      if (!v) return "Subject is required";
      if (v.length < 5) return "Subject must be at least 5 characters";
      if (v.length > 200) return "Subject must not exceed 200 characters";
      if (!SUBJECT_REGEX.test(v))
        return "Subject can contain only letters, numbers, spaces and . , - & ' ( ) / : ; ? !";
      return "";

    case "person":
      if (!v) return "Related person name is required";
      if (v.length < 3) return "Name must be at least 3 characters";
      if (v.length > 100) return "Name must not exceed 100 characters";
      if (!NAME_REGEX.test(v)) return "Name can contain only alphabets, spaces, '.' and '''";
      return "";

    case "voterId":
      if (!v) return ""; // optional
      if (!VOTER_ID_REGEX.test(v))
        return "Enter a valid Voter ID (3 letters + 7 digits, e.g. ABC1234567)";
      return "";

    case "mobile":
      if (!v) return ""; // optional
      if (!/^\d+$/.test(v)) return "Mobile number must contain digits only";
      if (v.length !== 10) return "Mobile number must be exactly 10 digits";
      if (!MOBILE_REGEX.test(v)) return "Mobile number must start with 6, 7, 8 or 9";
      return "";

    case "category":
      if (!v) return "Please select a Category";
      if (!CATEGORIES.includes(v)) return "Please select a valid Category";
      return "";

    case "priority":
      if (!v) return "Please select a Priority";
      if (!PRIORITIES.includes(v)) return "Please select a valid Priority";
      return "";

    case "dueDate": {
      if (!v) return "Due date is required";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "Enter a valid date";
      if (d.getFullYear() < 1990) return "Date cannot be before 1990";
      if (d.getFullYear() > 2100) return "Date cannot be after 2100";
      return "";
    }

    case "status":
      if (!v) return "Please select a Status";
      if (!STATUSES.includes(v)) return "Please select a valid Status";
      return "";

    case "village":
      if (!v) return ""; // optional
      if (!VILLAGE_REGEX.test(v)) return "Village name can contain only alphabets (A-Z)";
      if (v.length < 2) return "Village name must be at least 2 characters";
      if (v.length > 100) return "Village name must not exceed 100 characters";
      return "";

    case "mandalId":
      if (!v) return ""; // optional
      if (!MANDALS.some((m) => String(m.id) === String(v)))
        return "Please select a valid Mandal";
      return "";

    case "description":
      if (!v) return "Notes / description is required";
      if (v.length < 10) return "Description must be at least 10 characters";
      if (v.length > 1000) return "Description must not exceed 1000 characters";
      return "";

    default:
      return "";
  }
};

const validateAll = (data) => {
  const errors = {};
  FIELD_ORDER.forEach((name) => {
    const msg = validateField(name, data[name]);
    if (msg) errors[name] = msg;
  });
  return errors;
};

const NotesFollowUpForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(""); // server / API errors only

  const showError = (name) => touched[name] && errors[name];
  const inputClass = (name) => (showError(name) ? "input-error" : "");

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;

    // Auto-format specific fields
    if (name === "voterId") value = value.toUpperCase().replace(/\s/g, "");
    if (name === "mobile") value = value.replace(/\D/g, "");

    setFormData((prev) => ({ ...prev, [name]: value }));

    const isLive = LIVE_VALIDATE_FIELDS.includes(name);
    if (touched[name] || isLive) {
      if (isLive && !touched[name]) {
        setTouched((prev) => ({ ...prev, [name]: true }));
      }
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleReset = () => {
    setFormData(INITIAL_FORM);
    setErrors({});
    setTouched({});
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate every field and mark all as touched so every error is shown
    const newErrors = validateAll(formData);
    setErrors(newErrors);
    setTouched(FIELD_ORDER.reduce((acc, k) => ({ ...acc, [k]: true }), {}));

    const firstInvalid = FIELD_ORDER.find((f) => newErrors[f]);
    if (firstInvalid) {
      toast.error("Please fix the highlighted fields");
      const el = document.querySelector(`[name="${firstInvalid}"]`);
      if (el) el.focus();
      return;
    }

    setSubmitting(true);
    try {
      const mandalName = formData.mandalId
        ? MANDALS.find((m) => String(m.id) === String(formData.mandalId))?.name
        : null;

      await notesFollowupsAPI.create({
        subject: formData.subject.trim(),
        related_person: formData.person.trim(),
        epic_no: formData.voterId.trim() || null,
        mobile_number: formData.mobile.trim() || null,
        category: formData.category,
        priority: PRIORITY_TO_BACKEND[formData.priority] ?? null,
        due_date: formData.dueDate, // <input type="date"> already gives YYYY-MM-DD
        status: STATUS_TO_BACKEND[formData.status] ?? "open",
        village_name: formData.village.trim() || null,
        mandal_name: mandalName || null, // schema wants a name; resolved from the chosen id
        notes_description: formData.description.trim(),
      });

      // Success toast — stays visible after navigating to the table page
      toast.success("Form submitted successfully", { toastId: "note-form-success" });

      handleReset();
      navigate("/dashboard/NotesFollowUpTable");
    } catch (err) {
      console.error("Note save failed:", err);
      setError(
        err.response?.data?.detail || err.message || "Something went wrong while saving the note."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="note-form-container">
      <div className="note-form-card">
        <div className="note-form-header">
          <h2>Add New Note / Follow Up</h2>
          <p>Track pending actions, reminders, and constituent follow-ups</p>
          <button
            type="button"
            className="note-back-btn"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
        </div>

        {error && <div className="note-form-error">{error}</div>}

        {/* noValidate -> disables browser default popups so our custom messages show */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="note-form-grid">

            <div className="note-form-group note-full-width">
              <label>Subject *</label>
              <input
                type="text"
                name="subject"
                placeholder="Ex: Follow up on drinking water pipeline complaint"
                value={formData.subject}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("subject")}
                maxLength={200}
              />
              {showError("subject") && <span className="error-text">{errors.subject}</span>}
            </div>

            <div className="note-form-group">
              <label>Related Person *</label>
              <input
                type="text"
                name="person"
                placeholder="Enter Name"
                value={formData.person}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("person")}
                maxLength={100}
              />
              {showError("person") && <span className="error-text">{errors.person}</span>}
            </div>

            <div className="note-form-group">
              <label>Voter ID (Optional)</label>
              <input
                type="text"
                name="voterId"
                placeholder="Enter Voter ID (e.g. ABC1234567)"
                value={formData.voterId}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("voterId")}
                maxLength={10}
              />
              {showError("voterId") && <span className="error-text">{errors.voterId}</span>}
            </div>

            <div className="note-form-group">
              <label>Mobile Number (Optional)</label>
              <input
                type="tel"
                inputMode="numeric"
                name="mobile"
                placeholder="Enter 10-digit Mobile Number"
                maxLength="10"
                value={formData.mobile}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("mobile")}
              />
              {showError("mobile") && <span className="error-text">{errors.mobile}</span>}
            </div>

            <div className="note-form-group">
              <label>Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("category")}
              >
                <option value="">Select Category</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {showError("category") && <span className="error-text">{errors.category}</span>}
            </div>

            <div className="note-form-group">
              <label>Priority *</label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("priority")}
              >
                <option value="">Select Priority</option>
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              {showError("priority") && <span className="error-text">{errors.priority}</span>}
            </div>

            <div className="note-form-group">
              <label>Due Date *</label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("dueDate")}
                min="1990-01-01"
                max="2100-12-31"
              />
              {showError("dueDate") && <span className="error-text">{errors.dueDate}</span>}
            </div>

            <div className="note-form-group">
              <label>Status *</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("status")}
              >
                <option value="">Select Status</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {showError("status") && <span className="error-text">{errors.status}</span>}
            </div>

            

            <div className="note-form-group">
              <label>Mandal (Optional)</label>
              <select
                name="mandalId"
                value={formData.mandalId}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("mandalId")}
              >
                <option value="">Choose Mandal</option>
                {MANDALS.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
              {showError("mandalId") && <span className="error-text">{errors.mandalId}</span>}
            </div>



            <div className="note-form-group">
              <label>Village (Optional)</label>
              <input
                type="text"
                name="village"
                placeholder="Enter Village (alphabets only)"
                value={formData.village}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("village")}
                maxLength={100}
              />
              {showError("village") && <span className="error-text">{errors.village}</span>}
            </div>

          </div>

          <div className="note-form-group note-full-width">
            <label>Notes / Description *</label>
            <textarea
              rows="4"
              name="description"
              placeholder="Enter detailed notes about this follow-up"
              value={formData.description}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("description")}
              maxLength={1000}
            ></textarea>
            {showError("description") && <span className="error-text">{errors.description}</span>}
            <small className="char-count">{formData.description.length}/1000</small>
          </div>

          <div className="note-btn-group">
            <button type="submit" className="note-submit-btn" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit"}
            </button>
            <button type="button" className="note-reset-btn" onClick={handleReset}>
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NotesFollowUpForm;